/**
 * OTOMATİK YEDEK SAĞLAYICISI — Y şeridi.
 *
 * `RepositoryProvider`ın içinde durur, depo açıldıktan SONRA çizilir. Üç tetik:
 *   1. ilk çizim (açılış) → son yedekten beri değişen defter yazılır,
 *   2. depo `revision`ı değişti → gecikmeli yedek,
 *   3. `visibilitychange` → `hidden` → bekleyen yedek HEMEN.
 *
 * Kurulum ve geri yükleme de buradan geçer: açılır pencere / klasör izni
 * kullanıcı hareketi istediği için ekran bileşeni bu işlevleri tıklama
 * işleyicisinin İÇİNDEN, ilk iş olarak çağırıyor.
 */

import { M } from "../dil";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { ImportResult, KilitSirri } from "@parafon/contracts/local-api";
import { decryptPfai, deriveBackupKey, encryptPfaiWithKey, readPfaiHeader } from "../lib/pfai";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { beklemeMesaji, kilitDenemeSayaci, yanlisParolaMi } from "../lib/denemeSiniri";
import { hedefAc, hedefeBaglan, hedefSecenekleri } from "./hedefler";
import { eskiKasayiKapat, yeniKasayaKopyala } from "./hedefler/parafonSunucu";
import type { SunucuSecimi } from "./hedefler/parafonSunucu";
import type { HedefSecenegi } from "./hedefler";
import { createDefterKasasi, createIndexedDbTutamacDeposu, createIndexedDbKasa } from "./kasa";
import type { YedekKasasi } from "./kasa";
import { acilirPencereIleYetkilendir, bulutIstemcileri } from "./oauth";
import type { BulutBagimliliklari, BulutIstemcileri } from "./oauth";
import type { BulutDosyasi, BulutHedefi, HedefKaydi, HedefTuru, YedekAnahtari } from "./tipler";
import { cihazEtiketi, createYedekleyici, defterOzeti } from "./yedekleyici";
import type { YedekDurumu } from "./yedekleyici";

/** Kurulmamış bir hedefe geçici bağlantı — geri yükleme listesi için. */
export interface GeciciHedef {
  readonly kayit: HedefKaydi;
  readonly hedef: BulutHedefi;
}

export interface YedekBaglami {
  readonly durum: YedekDurumu;
  /** Defterde canlı kayıt yok (`null` = henüz bakılmadı). */
  readonly defterBos: boolean | null;
  readonly secenekler: readonly HedefSecenegi[];
  /**
   * KİLİTLİ DEFTER: yedek defterin ana anahtarıyla şifrelenir; ayrı yedek
   * parolası YOK (D3). Geri yüklemede parola YA DA 24 kelime.
   */
  readonly kilitli: boolean;
  /**
   * KULLANICI HAREKETİNDEN: hedefe bağlanır (açılır pencere / klasör seçici).
   * Parafon sunucusunda `sunucu`: yeni kod mu, var olan kod mu.
   */
  baglan(tur: HedefTuru, sunucu?: SunucuSecimi): Promise<GeciciHedef>;
  /** Kurulu hedef Parafon sunucusuysa kanonik kasa kodu; değilse null. */
  sunucuKodu(): Promise<string | null>;
  /**
   * KOD YENİLEME: yeni klasör, dosyalar kopyalanır, bu cihaz yeni koda geçer,
   * ANCAK ondan sonra eski klasör silinir. Yeni kanonik kod döner.
   */
  koduYenile(): Promise<string>;
  /** Öbür cihazda yenilenen kodu bu cihaza tanıtır (kod sunucuda doğrulanır). */
  koduDegistir(kod: string): Promise<void>;
  /**
   * DEPOLAMAYI DEĞİŞTİR (kullanıcı kararı 19.10.2026): kurulu depolama yeni
   * hedefe geçer, ilk kopya hemen yazılır. Eski hedef Parafon sunucusuysa ve
   * `sunucuyuSil` ise sunucudaki klasör ANCAK yeni kopya yazıldıktan sonra
   * silinir. Kullanıcının kendi bulutundaki dosyalara dokunulmaz.
   */
  depolamayiDegistir(h: GeciciHedef, sunucuyuSil: boolean): Promise<void>;
  /** "Yalnızca bu cihazda"ya dön: otomatik kopya durur; sunucu klasörü istenirse silinir. */
  yalnizcaCihaza(sunucuyuSil: boolean): Promise<void>;
  /**
   * Açık kasanın parolası mı (sunucu hedefindeki 12 karakter şartı için).
   * Kilitli kasa yoksa `null` (eski kip: ayrı yedek parolası formda girilir).
   */
  parolaDogrula(parola: string): Promise<boolean> | null;
  /** Bağlanmış hedefle otomatik yedeği başlatır. Eski kipte paroladan anahtar türetilir; kilitli defterde parola yok. */
  kur(hedef: GeciciHedef, parola?: string, tuz?: string): Promise<void>;
  /** Kurulu hedefi geçici hedef olarak verir (geri yükleme listesi için). */
  kuruluHedef(): Promise<GeciciHedef | null>;
  listele(h: GeciciHedef): Promise<readonly BulutDosyasi[]>;
  /**
   * Dosyayı çözer ve deftere aktarır. Eski kipte dosyanın tuzu döner
   * (sürdürmek için). Kilitli defterde `anahtarBenimsendi`: boş deftere v2
   * yüklendi, cihaz o yedeğin parolasını ve 24 kelimesini kullanıyor (D5).
   */
  geriYukle(
    h: GeciciHedef,
    ad: string,
    sir: string | KilitSirri,
  ): Promise<{ sonuc: ImportResult; tuz: string | null; anahtarBenimsendi: boolean }>;
  /**
   * D6 · DEFTERİ DEĞİŞTİR (yalnızca kilitli defter): dosyadaki defter bu
   * cihazdaki defterin YERİNE geçer. Otomatik yedek kuruluysa önce bekleyen
   * yedek ESKİ defterle boşaltılır. Otomatik yedek AÇIK KALIR: bulut adının
   * defter parçası defterin anahtarından türediği için yeni defter yeni
   * adlara yazılır, eski defterin bulut dosyaları ezilmez (eşitleme, 08.10).
   * Defter revizyonu bilerek ilerletilmez — arayüz sayfayı yeniden yükler.
   */
  defteriDegistir(h: GeciciHedef, ad: string, sir: KilitSirri): Promise<{ sonuc: ImportResult }>;
  simdi(): Promise<void>;
  yenidenBaglan(): Promise<void>;
  kaldir(): Promise<void>;
}

const BaglamY = createContext<YedekBaglami | null>(null);

/**
 * Yedeği parolayla açmak da kilit ekranıyla AYNI deneme sayacından geçer:
 * kasa kodunu ele geçiren biri parolayı uygulamanın içinden de denemesin
 * (kullanıcı kararı 16.10.2026). Asıl koruma yine yavaş parola çözme.
 */
async function sinirli<T>(dene: () => Promise<T>): Promise<T> {
  const kalan = kilitDenemeSayaci.kalanMs();
  if (kalan > 0) throw new Error(beklemeMesaji(kalan));
  try {
    const r = await dene();
    kilitDenemeSayaci.dogru();
    return r;
  } catch (e: unknown) {
    if (yanlisParolaMi(e)) kilitDenemeSayaci.yanlis();
    throw e;
  }
}

export function useYedek(): YedekBaglami | null {
  return useContext(BaglamY);
}

export interface YedekSaglayiciProps {
  readonly children: ReactNode;
  /** Testler bellek kasası geçirir; üretimde IndexedDB. */
  readonly kasa?: YedekKasasi;
  readonly bulut?: BulutBagimliliklari;
  readonly istemci?: BulutIstemcileri;
  readonly sifrele?: (json: string, a: YedekAnahtari) => Promise<string>;
  readonly anahtarTuret?: (parola: string, tuz?: string) => Promise<YedekAnahtari>;
  readonly coz?: (metin: string, parola: string) => Promise<string>;
  readonly tuzOku?: (metin: string) => Promise<string>;
  readonly gecikmeMs?: number;
}

/** Sekme öne gelince eşitleme turları arasındaki en kısa süre. */
export const ESITLEME_ARALIGI_MS = 60_000;

export const VARSAYILAN_BULUT: BulutBagimliliklari = {
  fetch: (...a) => globalThis.fetch(...a),
  yetkilendir: acilirPencereIleYetkilendir,
  saat: () => new Date(),
};

export function YedekSaglayici({
  children,
  kasa,
  bulut = VARSAYILAN_BULUT,
  istemci,
  sifrele = encryptPfaiWithKey,
  anahtarTuret = (p, tuz) => deriveBackupKey(p, tuz === undefined ? undefined : { salt: tuz }),
  coz = decryptPfai,
  tuzOku = async (m) => (await readPfaiHeader(m)).kdf.salt,
  gecikmeMs,
}: YedekSaglayiciProps) {
  const { repo, info, revision, invalidate } = useRepositoryContext();
  const sifreli = repo.sifreliYedek;
  const istemciler = useMemo(() => istemci ?? bulutIstemcileri(), [istemci]);
  const kilitP = repo.kilit;
  const kasaR = useMemo(() => {
    if (kasa !== undefined) return kasa;
    if (kilitP === undefined || sifreli === undefined) return createIndexedDbKasa();
    // Kilitli defter: kasa profilin şifreli kabında (çoklu profil, Adım 2).
    return createDefterKasasi(kilitP, {
      tutamaclar: createIndexedDbTutamacDeposu(),
      eski: {
        kasa: createIndexedDbKasa(),
        buProfilEnEskiMi: async () => {
          const d = await kilitP.durum();
          return d.acik && d.profil !== null && d.profiller[0]?.id === d.profil.id;
        },
      },
    });
  }, [kasa, kilitP, sifreli]);
  // Yedekleyici bir kez kurulur; `invalidate` her çizimde yeni olabilir.
  const invalidateRef = useRef(invalidate);
  invalidateRef.current = invalidate;

  const yedekleyici = useMemo(
    () =>
      createYedekleyici({
        kasa: kasaR,
        hedefAc: (k) => hedefAc(k, istemciler, bulut),
        disaAktar: () => repo.exportPlainJson(),
        sifrele,
        ...(sifreli === undefined
          ? {}
          : {
              sifreliOlustur: () => sifreli.olustur(),
              dosyaOneki: () => sifreli.dosyaOneki(),
              esitle: (m: string) => sifreli.esitle(m),
              aitMi: (m: string) => sifreli.aitMi(m),
              esitlemeIziniIlerlet: () => sifreli.esitlemeIziniIlerlet(),
              esitlendi: () => invalidateRef.current(),
            }),
        cihaz: cihazEtiketi(info.deviceId),
        ...(gecikmeMs === undefined ? {} : { gecikmeMs }),
      }),
    // `sifrele` testte sabit; üretimde modül işlevi. Bağımlılıkta tutmak her
    // çizimde yeni yedekleyici kurardı.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kasaR, istemciler, bulut, repo, info.deviceId, sifreli],
  );

  const [durum, setDurum] = useState<YedekDurumu>(() => yedekleyici.durum());
  const [defterBos, setDefterBos] = useState<boolean | null>(null);

  useEffect(() => {
    setDurum(yedekleyici.durum());
    const birak = yedekleyici.abone(setDurum);
    void yedekleyici.baslat();
    return () => {
      birak();
      yedekleyici.kapat();
    };
  }, [yedekleyici]);

  // Defter değişti → gecikmeli yedek. İlk çizimdeki revision atlanıyor:
  // açılış tetiğini `baslat` zaten karşılıyor.
  const ilkRevision = useRef(revision);
  useEffect(() => {
    if (revision === ilkRevision.current) return;
    yedekleyici.degisti();
  }, [revision, yedekleyici]);

  // "Otomatik yedek kapalı" uyarısı yalnızca DOLU defterde anlamlı.
  useEffect(() => {
    let iptal = false;
    repo
      .exportPlainJson()
      .then((j) => defterOzeti(j))
      .then((o) => {
        if (!iptal) setDefterBos(o.bos);
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [repo, revision]);

  useEffect(() => {
    const doc = globalThis.document;
    if (doc === undefined) return;
    // Sekme öne gelince eşitleme — en fazla dakikada bir (her sekme
    // değiştirişte buluta gitmesin).
    let sonEsitleme = Date.now();
    const dinle = (): void => {
      if (doc.visibilityState === "hidden") {
        void yedekleyici.bosalt();
        return;
      }
      if (Date.now() - sonEsitleme < ESITLEME_ARALIGI_MS) return;
      sonEsitleme = Date.now();
      void yedekleyici.esitle();
    };
    doc.addEventListener("visibilitychange", dinle);
    return () => doc.removeEventListener("visibilitychange", dinle);
  }, [yedekleyici]);

  const baglan = useCallback(
    async (tur: HedefTuru, sunucu?: SunucuSecimi): Promise<GeciciHedef> => {
      const kayit = await hedefeBaglan(tur, istemciler, bulut, sunucu);
      return { kayit, hedef: hedefAc(kayit, istemciler, bulut) };
    },
    [istemciler, bulut],
  );

  const deger = useMemo<YedekBaglami>(
    () => ({
      durum,
      defterBos,
      secenekler: hedefSecenekleri(istemciler),
      kilitli: sifreli !== undefined,
      baglan,
      async kur(h, parola, tuz) {
        if (sifreli !== undefined) {
          await yedekleyici.kur(null, h.hedef.kayit());
          return;
        }
        if (parola === undefined) throw new Error(M.yedek.saglayici.parolaGerekli);
        const anahtar = await anahtarTuret(parola, tuz);
        await yedekleyici.kur(anahtar, h.hedef.kayit());
      },
      async kuruluHedef() {
        const k = await kasaR.oku();
        if (k === null) return null;
        return { kayit: k.hedef, hedef: hedefAc(k.hedef, istemciler, bulut) };
      },
      async listele(h) {
        const liste = await h.hedef.listele();
        return [...liste].sort((a, b) => (b.degisti ?? "").localeCompare(a.degisti ?? ""));
      },
      async geriYukle(h, ad, sir) {
        const metin = await h.hedef.oku(ad);
        if (sifreli !== undefined) {
          const r = await sinirli(() => sifreli.geriYukle(metin, typeof sir === "string" ? { parola: sir } : sir));
          invalidate();
          return { sonuc: r.sonuc, tuz: null, anahtarBenimsendi: r.anahtarBenimsendi };
        }
        if (typeof sir !== "string" && !("parola" in sir)) throw new Error(M.yedek.saglayici.eskiYedekParolayla);
        const parola = typeof sir === "string" ? sir : sir.parola;
        const tuz = await tuzOku(metin);
        const json = await coz(metin, parola);
        const sonuc = await repo.importPlainJson(json);
        invalidate();
        return { sonuc, tuz, anahtarBenimsendi: false };
      },
      async defteriDegistir(h, ad, sir) {
        if (sifreli === undefined) throw new Error(M.yedek.saglayici.degistirmeKilitli);
        const metin = await h.hedef.oku(ad);
        // Bekleyen yedek ESKİ defterle yazılsın (değişimden sonra dışa aktarma
        // yeni defteri verirdi).
        if (durum.kurulu === true) await yedekleyici.bosalt();
        const r = await sinirli(() => sifreli.defteriDegistir(metin, sir));
        return { sonuc: r.sonuc };
      },
      async sunucuKodu() {
        const k = await kasaR.oku();
        return k !== null && k.hedef.tur === "parafon-sunucu" ? k.hedef.kod : null;
      },
      async koduYenile() {
        const k = await kasaR.oku();
        if (k === null || k.hedef.tur !== "parafon-sunucu") throw new Error(M.yedek.saglayici.sunucuDegil);
        const istek = istemciler.merkez;
        if (istek === null || istek === undefined) throw new Error(M.yedek.hedef.merkezYok);
        // Bekleyen yedek önce ESKİ klasöre: kopya en güncel hâli taşısın.
        await yedekleyici.bosalt();
        const yeni = await yeniKasayaKopyala(istek, k.hedef);
        await yedekleyici.hedefiDegistir(yeni);
        await eskiKasayiKapat(istek, k.hedef);
        return yeni.kod;
      },
      async koduDegistir(kod) {
        const kayit = await hedefeBaglan("parafon-sunucu", istemciler, bulut, { kod });
        await yedekleyici.hedefiDegistir(kayit);
        await yedekleyici.esitle();
      },
      async depolamayiDegistir(h, sunucuyuSil) {
        const eski = await kasaR.oku();
        // Bekleyen değişiklik önce ESKİ hedefe: hiçbir şey arada kalmasın.
        if (eski !== null) await yedekleyici.bosalt();
        const anahtar = sifreli === undefined && eski !== null && eski.surum === 1 ? eski.anahtar : null;
        if (sifreli === undefined && anahtar === null) throw new Error(M.yedek.saglayici.anahtarYok);
        await yedekleyici.kur(anahtar, h.hedef.kayit());
        if (sunucuyuSil && eski !== null && eski.hedef.tur === "parafon-sunucu") {
          const istek = istemciler.merkez;
          if (istek !== null && istek !== undefined) await eskiKasayiKapat(istek, eski.hedef);
        }
      },
      async yalnizcaCihaza(sunucuyuSil) {
        const eski = await kasaR.oku();
        if (eski !== null) await yedekleyici.bosalt();
        await yedekleyici.kaldir();
        if (sunucuyuSil && eski !== null && eski.hedef.tur === "parafon-sunucu") {
          const istek = istemciler.merkez;
          if (istek !== null && istek !== undefined) await eskiKasayiKapat(istek, eski.hedef);
        }
      },
      parolaDogrula: (parola) => kilitP?.parolaDogrula?.(parola) ?? null,
      simdi: () => yedekleyici.simdi(),
      yenidenBaglan: () => yedekleyici.yenidenBaglan(),
      kaldir: () => yedekleyici.kaldir(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [durum, defterBos, istemciler, baglan, yedekleyici, kasaR, bulut, repo, invalidate, sifreli, kilitP],
  );

  return <BaglamY.Provider value={deger}>{children}</BaglamY.Provider>;
}
